import { Card, CardButtonActionMode, HeaderGradient, MiniCard, Row } from '@app/components';
import React, { memo, Fragment } from 'react';
import { theme } from '@app/theme';
import { useDynamicSizes } from '@app/hooks';
import LinearGradient from 'react-native-linear-gradient';
import { StyleSheet } from 'react-native';
import { IPaymentMethod, getPaymentTypeFR } from '@app/common';
import { useAppSelector } from '@app/store/hooks';
import { triptychSelectors } from '@app/store';
import styled from '@2pulse/responsive';
import { normalize } from '@app/helpers';
import { SlantTitle } from '@app/components/typography';
import SvgStats from '@app/components/icons/SvgStats';
import * as R from 'remeda';

type PaymentMethodCardProps = {
  paymentMethod?: Partial<IPaymentMethod>;
  toggle: (mode: CardButtonActionMode) => void;
};

const formatCardData = (partialPaymentMethod: Partial<IPaymentMethod>) => {
  return {
    name: partialPaymentMethod?.name || 'Nouveau moyen de paiement'
  }
}

export const PaymentMethodCard = memo(({ paymentMethod, toggle }: PaymentMethodCardProps) => {
  const { subContainerHeight } = useDynamicSizes();

  const partialPaymentMethod = useAppSelector<Partial<IPaymentMethod>>(triptychSelectors.selectData);
  const cardData = useAppSelector<Partial<IPaymentMethod>>(triptychSelectors.selectCardData);

  const { name } = formatCardData(cardData ?? partialPaymentMethod);

  return (
    <Card>

      <HeaderGradient>
        <LinearGradient
          start={{ x: 0.5, y: 0 }}
          end={{ x: 0.5, y: 1 }}
          locations={[0, 1]}
          colors={['rgba(253, 255, 252, 0)', 'rgba(253, 255, 252, 1)']}
          style={styles.headerGradient}
        />
      </HeaderGradient>

      <Card.TopHeader>
        <Card.TopHeaderLabel>Fiche moyen de paiement</Card.TopHeaderLabel>
      </Card.TopHeader>

      <Card.Container height={subContainerHeight}>
        <Card.Background source={require('@app/assets/img/cardbackground.png')} />

        <Card.Content>
          <Card.Header>
            {/* En-tête */}
            <Row justify="space-between" align="center">
              <Card.HeaderTitles>
                <Card.Title numberOfLines={2}>{name}</Card.Title>
                {paymentMethod?._id && <Card.Subtitle>Ref: {paymentMethod._id}</Card.Subtitle>}
              </Card.HeaderTitles>

              <Card.ButtonAction onPress={toggle} permissionScope="payment-methods" />
            </Row>
          </Card.Header>
        </Card.Content>

        {!R.isEmptyish(paymentMethod) && (
          <MiniCard>
            <MiniCard.Tabs>
              <MiniCard.TabsHolder>
                <MiniCard.Tab Icon={SvgStats} isActive />
              </MiniCard.TabsHolder>
            </MiniCard.Tabs>
            <MiniCard.Container>
              <MiniCard.Content>

                <Card.LineBreak height={16} />

                <SlantTitle label="Informations" color={theme.colors.gold} />

                <Card.HelpScrollContainer extraStyle={{ paddingLeft: 24, paddingRight: 24, paddingTop: 60 }}>

                  {/* Informations générales */}
                  <InfoSection>
                    <SectionTitle>💳 Informations générales</SectionTitle>
                    <InfoGrid>
                      {paymentMethod.name && (
                        <InfoRow>
                          <InfoLabel>Nom</InfoLabel>
                          <InfoValue>{paymentMethod.name}</InfoValue>
                        </InfoRow>
                      )}
                      {paymentMethod.accountingCode !== undefined && (
                        <InfoRow>
                          <InfoLabel>Code comptable</InfoLabel>
                          <InfoValue>{paymentMethod.accountingCode}</InfoValue>
                        </InfoRow>
                      )}
                      {paymentMethod.specialType && (
                        <InfoRow>
                          <InfoLabel>Type de paiement</InfoLabel>
                          <InfoValue>{getPaymentTypeFR(paymentMethod.specialType)}</InfoValue>
                        </InfoRow>
                      )}
                      {paymentMethod.disabled !== undefined && (
                        <InfoRow>
                          <InfoLabel>Statut</InfoLabel>
                          <StatusBadge active={!paymentMethod.disabled}>
                            {paymentMethod.disabled ? '❌ Inactif' : '✅ Actif'}
                          </StatusBadge>
                        </InfoRow>
                      )}
                    </InfoGrid>
                  </InfoSection>

                  <Card.LineBreak height={24} />

                </Card.HelpScrollContainer>
              </MiniCard.Content>
            </MiniCard.Container>
          </MiniCard>
        )}
      </Card.Container>
    </Card>
  );
});


const styles = StyleSheet.create({
  headerGradient: {
    flex: 1,
    height: theme.sizes.listHeaderHeight,
  },
});

// Styled Components
const InfoSection = styled.View({
  marginBottom: normalize(28),
});

const SectionTitle = styled.Text({
  color: theme.colors.babypowder,
  fontSize: normalize(18),
  fontWeight: 'bold',
  marginBottom: normalize(16),
  letterSpacing: 0.3,
});

const InfoGrid = styled.View({
  gap: normalize(12),
  backgroundColor: 'rgba(253, 255, 252, 0.03)',
  borderRadius: normalize(12),
  padding: normalize(16),
  borderWidth: 1,
  borderColor: 'rgba(253, 255, 252, 0.08)',
});

const InfoRow = styled.View({
  flexDirection: 'row',
  justifyContent: 'space-between',
  alignItems: 'center',
  paddingVertical: normalize(8),
});

const InfoLabel = styled.Text({
  color: theme.colors.grey,
  fontSize: normalize(14),
  fontWeight: '500',
  flex: 1,
});

const InfoValue = styled.Text({
  color: theme.colors.babypowder,
  fontSize: normalize(15),
  fontWeight: '600',
  flex: 1,
  textAlign: 'right',
});

const StatusBadge = styled.Text<{ active: boolean }>(({ props }) => ({
  color: props.active ? theme.colors.gold : theme.colors.crimson,
  fontSize: normalize(15),
  fontWeight: 'bold',
  flex: 1,
  textAlign: 'right',
}));
